import React from 'react'

import { ButtonClean, Subsection, useModalContext } from '@fourthwall/components'
import { useWebViewBridge } from '@fourthwall/webview-bridge'
import {
  AddSubscriptionModal,
  UpgradeSubscriptionBanner,
  useSubscription,
} from '@modules/Dashboard/modules/Subscriptions'
import { routing } from '@utils/routing'

import * as S from './ClaimDomain.styled'
import { ConfirmClaimDomainModal } from './components/ConfirmClaimDomainModal/ConfirmClaimDomainModal'
import { CurrentDomain } from './components/CurrentDomain/CurrentDomain'
import { DomainSuggestions } from './components/DomainSuggestions/DomainSuggestions'
import { SearchDomainInput } from './components/SearchDomainInput/SearchDomainInput'
import { usePaidDomain } from './hooks/usePaidDomain'
import { useSearchDomain } from './hooks/useSearchDomain'

export const ClaimDomain = () => {
  const { customDomains, canUpgradeSubscription } = useSubscription()
  const { search, suggestions, isDomainAvailable, isSearching, setSearch } = useSearchDomain()
  const { currentDomain, isRegistering, registerDomain } = usePaidDomain()
  const { open } = useModalContext()
  const { isWebView } = useWebViewBridge()

  const claimButtonLabel = !customDomains.canCreate ? 'Claim with Pro' : 'Claim for free'

  const openConfirmClaimDomainModal = (domain: string) => {
    open(ConfirmClaimDomainModal, {
      domain,
      onConfirm: async () => {
        await registerDomain(domain)
      },
    })
  }

  const handleUpgradeClick = (domain?: string) => {
    open(AddSubscriptionModal, {
      title: 'Upgrade your plan to claim your free domain',
      source: 'settings_domain',
      onSuccess: () => {
        if (domain) {
          openConfirmClaimDomainModal(domain)
        }
      },
    })
  }

  const handleClaimDomain = (domain: string) => {
    if (!customDomains.canCreate) {
      return handleUpgradeClick(domain)
    }

    openConfirmClaimDomainModal(domain)
  }

  const getCurrentDomainTag = () => {
    if (isDomainAvailable) {
      const label = !customDomains.canCreate ? 'Available on Pro' : 'Available'

      return { label, appearance: 'success' as const }
    }

    return { label: 'Unavailable', appearance: 'alert' as const }
  }

  if (currentDomain || (!customDomains.canCreate && !canUpgradeSubscription)) {
    return null
  }

  const renderUpgradeBanner = () => {
    if (isWebView) return null

    if (!canUpgradeSubscription || customDomains.canCreate) return null

    return (
      <UpgradeSubscriptionBanner source="settings_domain" onClick={() => handleUpgradeClick()}>
        Upgrade to <strong>Fourthwall Pro</strong> and claim your free custom domain plus unlock
        dozens of powerful perks.{' '}
        <ButtonClean
          appearance="inherit"
          label="Learn more"
          target="_blank"
          href={routing.external.helpCenter.subscriptionDomains}
        />
      </UpgradeSubscriptionBanner>
    )
  }

  return (
    <Subsection
      title={canUpgradeSubscription ? 'Claim free domain with Fourthwall Pro' : 'Claim free domain'}
    >
      <S.Container>
        {renderUpgradeBanner()}

        {(customDomains.canCreate || !canUpgradeSubscription) && (
          <div>
            Search for the domain (.store, .tech, .online) to claim for free.{' '}
            <ButtonClean
              appearance="inherit"
              label="Learn more"
              target="_blank"
              href={routing.external.helpCenter.subscriptionDomains}
            />
          </div>
        )}

        <SearchDomainInput isSearching={isSearching} onSearch={setSearch} />

        {!isSearching && suggestions.length > 0 && (
          <CurrentDomain
            domain={search}
            tag={getCurrentDomainTag()}
            claimLabel={claimButtonLabel}
            isClaiming={isRegistering}
            onClaim={isDomainAvailable ? () => handleClaimDomain(search) : undefined}
          />
        )}

        {!isSearching && suggestions.length > 0 && (
          <DomainSuggestions
            suggestions={suggestions}
            claimLabel={claimButtonLabel}
            isClaiming={isRegistering}
            onClaim={(domain) => handleClaimDomain(domain)}
          />
        )}
      </S.Container>
    </Subsection>
  )
}
